import {
  AccessCatalogSchema,
  AccountSummaryListSchema,
  AccountWriteResponseSchema,
  CentralIdentityCandidateListSchema,
  DynamicRoleListSchema,
  DynamicRoleWriteResponseSchema,
  IdentityBindingResponseSchema,
  type AccountStatus,
  type CapabilityBaseline,
  type PageKey,
  type RoleStatus,
} from '@youkuai/contracts';

import { apiRequest } from './project-api.ts';

export async function listAccounts(token: string) {
  return AccountSummaryListSchema.parse(await apiRequest('/system/accounts', token));
}

export async function listCentralIdentities(token: string) {
  return CentralIdentityCandidateListSchema.parse(await apiRequest('/system/central-identities', token));
}

export async function bindCentralIdentity(token: string, accountId: string, input: {
  centralUserId: string;
  expectedVersion: number;
  reason: string;
}) {
  return AccountWriteResponseSchema.parse(
    await apiRequest(`/system/accounts/${encodeURIComponent(accountId)}/central-identity`, token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `account-central-identity-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
  );
}

export async function createAccount(token: string, input: {
  personId: string;
  username: string;
  roleIds: string[];
  note: string | null;
  reason: string;
}) {
  return AccountWriteResponseSchema.parse(await apiRequest('/system/accounts', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `account-create-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function updateAccount(token: string, accountId: string, input: {
  expectedVersion: number;
  username: string;
  note: string | null;
  reason: string;
}) {
  return AccountWriteResponseSchema.parse(await apiRequest(`/system/accounts/${encodeURIComponent(accountId)}`, token, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `account-update-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function listDynamicRoles(token: string) {
  return DynamicRoleListSchema.parse(await apiRequest('/system/roles', token));
}

export async function getAccessCatalog(token: string) {
  return AccessCatalogSchema.parse(await apiRequest('/system/access-catalog', token));
}

export async function createDynamicRole(token: string, input: {
  code: string;
  name: string;
  description: string | null;
  capabilityBaseline: Exclude<CapabilityBaseline, 'SYSTEM_ADMIN'>;
  pageKeys: PageKey[];
  reason: string;
}) {
  return DynamicRoleWriteResponseSchema.parse(await apiRequest('/system/roles', token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `role-create-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function cloneDynamicRole(token: string, roleId: string, input: {
  code: string;
  name: string;
  reason: string;
}) {
  return DynamicRoleWriteResponseSchema.parse(await apiRequest(`/system/roles/${encodeURIComponent(roleId)}/clone`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `role-clone-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function updateDynamicRole(token: string, roleId: string, input: {
  expectedVersion: number;
  name: string;
  description: string | null;
  capabilityBaseline: Exclude<CapabilityBaseline, 'SYSTEM_ADMIN'>;
  reason: string;
}) {
  return DynamicRoleWriteResponseSchema.parse(await apiRequest(`/system/roles/${encodeURIComponent(roleId)}`, token, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `role-update-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function changeDynamicRoleStatus(token: string, roleId: string, input: {
  expectedVersion: number;
  status: RoleStatus;
  reason: string;
}) {
  return DynamicRoleWriteResponseSchema.parse(await apiRequest(`/system/roles/${encodeURIComponent(roleId)}/status`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `role-status-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function replaceDynamicRolePages(token: string, roleId: string, input: {
  expectedVersion: number;
  pageKeys: PageKey[];
  reason: string;
}) {
  return DynamicRoleWriteResponseSchema.parse(await apiRequest(`/system/roles/${encodeURIComponent(roleId)}/pages`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `role-pages-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function replaceAccountDynamicRoles(token: string, accountId: string, input: {
  expectedVersion: number;
  roleIds: string[];
  reason: string;
}) {
  return AccountWriteResponseSchema.parse(await apiRequest(`/system/accounts/${encodeURIComponent(accountId)}/roles`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `account-roles-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function changeAccountStatus(token: string, accountId: string, input: {
  expectedVersion: number;
  status: AccountStatus;
  reason: string;
}) {
  return AccountWriteResponseSchema.parse(await apiRequest(`/system/accounts/${encodeURIComponent(accountId)}/status`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `account-status-${crypto.randomUUID()}` },
    body: JSON.stringify(input),
  }));
}

export async function completeIdentityBinding(token: string, accountId: string, input: {
  reason: string;
}) {
  return IdentityBindingResponseSchema.parse(
    await apiRequest(`/system/accounts/${encodeURIComponent(accountId)}/identity-link`, token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `account-identity-link-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
  );
}

export async function resetIdentityCredential(token: string, accountId: string, input: {
  reason: string;
}) {
  return IdentityBindingResponseSchema.parse(
    await apiRequest(`/system/accounts/${encodeURIComponent(accountId)}/identity-link/reset`, token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': `account-identity-reset-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
  );
}
